import { Label } from '@hqo/ui-atoms';
import { TimePicker } from '@hqo/date-time';
import { Controller, Control, FieldErrors } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { CreateVisitFormV2Values } from '../../../types/form-data.types';
import { useRef } from 'react';
import { useValidationMessage } from '../../../hooks/use-validation-message.hook';
import { useLocale } from 'shared/hooks/use-locale/use-locale.hook';

interface TimeInputFieldProps {
  name: 'startTime' | 'endTime';
  control: Control<CreateVisitFormV2Values>;
  errors: FieldErrors<CreateVisitFormV2Values>;
  label: string;
  id: string;
  placeholder?: string;
  minTime?: string;
  required?: boolean;
}

export const TimeInputField = ({
  name,
  control,
  errors,
  label,
  id,
  placeholder,
  minTime,
  required = false,
}: TimeInputFieldProps): JSX.Element => {
  const previousValue = useRef<string | null>(null);
  const { formatMessage } = useIntl();
  const locale = useLocale();
  const formatValidationMessage = useValidationMessage();
  const error = errors[name];
  const defaultPlaceholder = formatMessage({
    id: 'createVisitFormV2.whenStep.selectTime',
    defaultMessage: 'Select time',
  });

  return (
    <div className="flex flex-col">
      {/* Label area - fixed height to align inputs */}
      {label && (
        <div className="mb-1.5 h-7 flex items-end">
          <Label htmlFor={id} className="gap-1">
            {label}
            {required && <span className="text-destructive">*</span>}
          </Label>
        </div>
      )}
      <Controller
        name={name}
        control={control}
        // eslint-disable-next-line react/jsx-no-bind
        render={({ field }) => (
          <>
            <div className="w-full">
              <TimePicker
                id={id}
                locale={locale}
                timeFormat="auto"
                className={error ? 'border-destructive' : ''}
                // eslint-disable-next-line react/jsx-no-bind
                onValueChange={(value) => {
                  // Convert null to empty string to avoid validation errors
                  const newValue = value === null ? '' : value || '';

                  if (previousValue.current !== newValue) {
                    previousValue.current = newValue;
                    field.onChange(newValue);
                  }
                }}
                placeholder={placeholder || defaultPlaceholder}
                timeInterval={30}
                value={field.value || ''}
                minTime={minTime || undefined}
              />
            </div>
            {/* Error message area - fixed height */}
            <div className="mt-1 h-5">
              {error && <p className="text-sm text-destructive">{formatValidationMessage(error.message as string)}</p>}
            </div>
          </>
        )}
      />
    </div>
  );
};
